import { Form } from "@unform/web";
import React, { useEffect, useRef, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { IoMdCheckmark } from "react-icons/io";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../../components/DivDiagonal";
import CustomButton from "../../../components/Form/Button";
import Input from "../../../components/Form/Input";
import messageTypes from "../../../constants/MessagesTypes";
import { createCompany, updateCompany, getCompany } from "../../../services/Company";
import {
  CompanyNewWrapper,
  CompanyCard,
  CardTitle,
  FormWrapper,
  StyledFormWrapper,
  ContentArea,
  ButtonContainer,
} from "./styles";

export default function NewCompany() {
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(undefined);
  const formRef = useRef(null);
  const history = useHistory();
  const { id } = useParams();
  const { isAdmin } = useSelector((state) => state.CompanyReducer || { isAdmin: false });

  async function handleSubmit(data) {
    try {
      if (loading) {
        return;
      }

      if (!isAdmin) {
        toast.error('Apenas administradores podem criar/editar empresas.');
        return;
      }

      setLoading(true);
      formRef.current.setErrors({});
      const schema = Yup.object().shape({
        name: Yup.string().required(messageTypes.REQUIRED_FIELD),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const companyData = {
        name: data.name,
        active: data.active !== undefined ? data.active : true,
      };

      const response = id 
        ? await updateCompany(id, companyData) 
        : await createCompany(companyData);

      if (response && response.status === 200) {
        toast.success(id ? messageTypes.SUCCESS_UPDATE : messageTypes.SUCCESS);
        history.push('/company');
      } else {
        toast.error(id ? messageTypes.ERROR_UPDATE : messageTypes.ERROR_SAVE);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(err.response?.data?.message || messageTypes.ERROR);
    }
  }

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem acessar esta página.');
      history.push('/dashboard');
      return;
    }

    if (id) {
      setLoading(true);
      getCompany(id).then((data) => {
        if (data && data.data && data.data.data) {
          setCompany(data.data.data);
        }
      }).catch(() => {
        history.push('/company');
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setCompany(null);
      setLoading(false);
    }
  }, [id, isAdmin, history]);

  if (!isAdmin) {
    return null;
  }

  return (
    <CompanyNewWrapper>
      <DivDiagonal />
      <ContentArea>
        {company !== undefined && (
          <CompanyCard>
            <StyledFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit} initialData={company}>
                <CardTitle>
                  {id ? (
                    <>
                      <i className="fas fa-edit" /> Editar Empresa
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus" /> Nova Empresa
                    </>
                  )}
                </CardTitle>

                <div className="row mb-4">
                  <div className="col-12">
                    <Input
                      name="name"
                      label="Nome da Empresa"
                      placeholder="Digite o nome da empresa"
                    />
                  </div>
                </div>

                <ButtonContainer>
                  <Link to="/company" style={{ textDecoration: 'none' }}>
                    <CustomButton
                      variant="outlined"
                      className="btn btn-secondary font-weight-bold text-capitalize mobile-button"
                      type="button"
                      startIcon={<BiArrowBack/>}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        color: '#e0e0e0',
                        padding: '14px 28px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Voltar
                    </CustomButton>
                  </Link>
                  <CustomButton
                    className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                    loading={loading}
                    type="submit"
                    startIcon={<IoMdCheckmark/>}
                    style={{
                      background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: '14px 32px',
                      borderRadius: '10px',
                      fontWeight: 600,
                      boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                    }}
                  >
                    Salvar
                  </CustomButton>
                </ButtonContainer>
              </Form>
            </StyledFormWrapper>
          </CompanyCard>
        )}
      </ContentArea>
    </CompanyNewWrapper>
  );
}

